import { Box, Typography, Grid, Paper, styled, Stack, Button, CircularProgress } from "@mui/material";
import DoneIcon from '@mui/icons-material/Done';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import jwtAxios from "../../utils/jwtAxios";
import "react-loading-skeleton/dist/skeleton.css";
import { AppColor } from "../../types/Constants";
import { useState } from "react";


export interface PricingTier {
    id: string;
    name: string;
    monthlyPrice: number;
    monthlyPriceId: string;
    yearlyPrice: number;
    yearlyPriceId: string;
    experienceLimit: number;
    beaconLimit: number;
    viewLimit: number;
    rank: number;
}

interface SubscriptionTableProps {
    currentSubscription: string;
    currentInterval: string;
    pricingTiers: PricingTier[];
    dashboardRequired?: boolean;
    loading?: boolean;
}

const SubscriptionTable: React.FC<SubscriptionTableProps> = ({ currentSubscription, currentInterval, pricingTiers, dashboardRequired = false, loading = false }) => {

    const [billingInterval, setBillingInterval] = useState<"month" | "year">(currentInterval === "month" || currentInterval === "year" ? currentInterval : "month");

    const tierMap: { [key: string]: { id: string, monthlyPrice: number, monthlyPriceId: string, yearlyPrice: number, yearlyPriceId: string, experienceLimit: number, beaconLimit: number, viewLimit: number, name: string, rank: number} } = {
        "Skidattl Essentials": { ...(pricingTiers.find(tier => tier.name.includes("Essentials")) ?? { id: "", monthlyPrice: 0, monthlyPriceId: "", yearlyPrice: 0, yearlyPriceId: "", experienceLimit: 0, beaconLimit: 0, viewLimit: 0 }), 
            name: "Essentials", 
            rank: 1
        },
        "Skidattl Professional": { ...(pricingTiers.find(tier => tier.name.includes("Professional")) ?? { id: "", monthlyPrice: 0, monthlyPriceId: "", yearlyPrice: 0, yearlyPriceId: "", experienceLimit: 0, beaconLimit: 0, viewLimit: 0 }), 
            name: "Professional",
            rank: 2
        },
        "Skidattl Enterprise": {...(pricingTiers.find(tier => tier.name.includes("Enterprise")) ?? { id: "", monthlyPrice: 0, monthlyPriceId: "", yearlyPrice: 0, yearlyPriceId: "", experienceLimit: 0, beaconLimit: 0, viewLimit: 0 }), 
            name: "Enterprise",
            rank: 3
        }
    };

    const features = {
        "Skidattl Essentials": ["1 User", "Basic Analytics", "White Glove Service"],
        "Skidattl Professional": ["2 Users", "Advanced Analytics", "Custom Branding", "Premium Beacons", "CSV Import", "Archiving Experiences", "White Glove Service"],
        "Skidattl Enterprise": ["3 Users", "Advanced Analytics", "Custom Branding", "Premium Beacons", "CSV Import", "Archiving Experiences", "Priority Access to New Features", "Prioritized Support", "White Glove Service"]
    };

    const displayLimits = (tier: PricingTier) => {

        const features = [
            { count: tier.experienceLimit, label: "Experience", pluralise: true },
            { count: tier.beaconLimit, label: "Beacon", pluralise: true },
            { count: tier.viewLimit, label: "Monthly Views per Experience", pluralise: false },
        ];

        return features.map((item, index) => {
            const label = item.pluralise && item.count > 1 ? `${item.label}s` : item.label;
            const count = item.count ?? 0;
            const formatCount = count.toLocaleString();

            return (
                <Stack key={index} alignItems="flex-start" direction="row" gap={3} marginLeft={5}>
                    <DoneIcon sx={{ color: "green", mt: 1 }} />
                    <Typography variant="body1" sx={{ lineHeight: 2 }}>
                        {formatCount} {label}
                    </Typography>
                </Stack>
            );
        });
    };

    const buttonLogic = (tierKey: string) => {
        if (currentSubscription === tierKey && (currentInterval == billingInterval || tierKey == "Test Drive")) {
            return dashboardRequired ? "Manage Subscription" : "Your Current Subscription";
        } else if (currentSubscription === tierKey && currentInterval != billingInterval) {
            return "Update Subscription";
        } else if (dashboardRequired && currentSubscription) {
            const subbedTier = tierMap[currentSubscription];
            const currentTier = tierMap[tierKey];
            return currentTier.rank <= (subbedTier?.rank ?? 4) ? "Downgrade Subscription" : "Upgrade Subscription";
        } else {
            return "Choose Subscription";
        }
    };
    
    const renderTier = (tierKey: string, features: string[]) => {
        const tier = tierMap[tierKey];
        return (
            <Grid item key={tierKey}>
                <Item sx={currentSubscription === tierKey && ( currentInterval == billingInterval || tierKey == "Test Drive" ) ? { maxWidth: "400px", flexGrow: 1, minHeight: "680px", display: "flex", flexDirection: "column", border: "solid 2px", borderImageSource: "linear-gradient(to bottom, rgba(255, 103, 31, 1) 50%, hsl(20, 100%, 50%) 70%)", borderImageSlice: 1}  : { flexGrow: 1, maxWidth: "400px", minHeight: "680px", display: "flex", flexDirection: "column"}}>
                    <Typography variant="h5" fontWeight={600}>{tier.name}</Typography>
                        <Box mb={6} borderBottom="solid" height={"100px"} sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
                        <Stack alignItems="center" direction="row" gap={2} justifyContent="center">
                            {loading ? (
                                <CircularProgress />
                            ) : (
                                <Typography variant="h3">${ billingInterval == "month" ? getPrice(tier.monthlyPrice) ?? 0 : getPrice(tier.yearlyPrice) ?? 0}</Typography>
                            )}
                            <Typography variant="body1">per {billingInterval}</Typography>
                        </Stack>
                        {tier.name != "Test Drive" && (
                             <Typography variant="body1" fontWeight={500} pt={1}>14 Day Free Trial</Typography>
                        )}

                    </Box>
                    <Box mb={8} minHeight={170} textAlign="left">
                        {displayLimits(tier)}
                        {features.map((feature, index) => (
                            <Stack key={index} alignItems="flex-start" direction="row" gap={3} marginLeft={5}>
                                {feature === "White Glove Service" ? (
                                    <MonetizationOnOutlinedIcon sx={{ color: AppColor.baseOrange, mt: 1 }} />
                                ) : (
                                    <DoneIcon sx={{ color: "green", mt: 1 }} />
                                )}
                                <Typography variant="body1" sx={{ lineHeight: 2}}>{feature}</Typography>
                            </Stack>
                        ))}
                    </Box>
                    <Box sx={{ marginTop: "auto", width: "100%", display: "flex", justifyContent: "center" }}>
                        <Button
                            variant="contained"
                            sx={{ paddingX: 4, width: "90%", whiteSpace: "nowrap", textAlign: "center"}}
                            onClick={() => {
                                if (dashboardRequired) {
                                    return manageSubscription(tier.id, currentSubscription === tierKey && currentInterval == billingInterval  ? "manage" : "upgrade");
                                    
                                } else {
                                    return currentSubscription !== tierKey
                                        ? manageSubscription(tier.id, "subscribe")
                                        : undefined;
                                }
                            }}
                            disabled={dashboardRequired ? false : currentSubscription === tierKey}
                        >
                            {buttonLogic(tierKey)}
                        </Button>
                    </Box>
                </Item>
            </Grid>
        );
    };    

    const manageSubscription = (tier: string, action: "manage" | "subscribe" | "upgrade") => {
        let apiUrl = "";
        if (tier == "test" && action == "upgrade") {
            // Downgrade on test driven cancel current subscription
             apiUrl = "/api/subscription/cancel";
        } else {
            switch (action) {
                case "manage":
                    apiUrl = `/api/subscription/manage`;
                    break;
                case "upgrade":
                    apiUrl = `/api/subscription/subscription-upgrade/?newProductId=${tier}&interval=${billingInterval}`;
                    break;
                case "subscribe":
                    apiUrl = `/api/subscription/${action}?productId=${tier}&interval=${billingInterval}`;
                    break;
            }
        }



        jwtAxios
            .post(apiUrl)
            .then(response => {
                window.location.href = response.data.url;
            })
            .catch(error => console.error("API request failed:", error));
    };
    

    const getPrice = (price: number) => {
        return price > 0 ? (price / 100).toLocaleString() : null;
    };

    const Item = styled(Paper)(({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        ...theme.typography.body1,
        padding: theme.spacing(3),
        textAlign: 'center',
      }));
      
      return (
        <Box sx={{
            width: '100%',
            background: "hsla(0, 0%, 97%, 1)",
            paddingX: 10,
            paddingTop: 5,
            paddingBottom: 10
        }}
        >
            <Typography paddingBottom={5} variant="h4" fontWeight={600}>Pricing Tiers</Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                <Box sx={{ border: "1px solid", borderRadius: 6, borderColor: AppColor.orangeBoldBackground, }}>
                <Button 
                    variant={billingInterval === "month" ? "contained" : "outlined"} 
                    onClick={() => setBillingInterval("month")} 
                    sx={{ borderRadius: 6, border: "none", minWidth: 120 }}
                >
                    Monthly
                </Button>
                <Button 
                    variant={billingInterval === "year" ? "contained" : "outlined"} 
                    onClick={() => setBillingInterval("year")} 
                    sx={{ borderRadius: 6, border: "none", minWidth: 120 }}
                >
                    Yearly
                </Button>
                </Box>
            </Box>
            <Grid
                container
                rowSpacing={2}
                columnSpacing={{ xs: 2, sm: 3, md: 4 }}
                alignItems="stretch"
                justifyContent="center"
            >
                {Object.entries(features).map(([key, featureList]) => (
                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                    key={key}
                    sx={{
                    display: "flex",
                    justifyContent: "center"
                    }}
                >
                    <Box sx={{
                        width: "100%",
                        maxWidth: 400,
                        display: "flex",
                        flexDirection: "column",
                        flexGrow: 1
                        }}
                    >
                    {renderTier(key, featureList)}
                    </Box>
                </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default SubscriptionTable;